import { getManufacturerFamilyIds, type SerializedManufacturer } from '@enmon/commons/lib/tools';
import type { ManufacturersApiFilter } from 'lib/services';
import { useMemo } from 'react';
import { useManufacturers } from './useManufacturers';

/**
 * One fixed filter so every caller shares a single react-query cache entry rather than refetching
 * the manufacturer list per page. `parent` is all the family rule needs.
 */
const FAMILY_FILTER: ManufacturersApiFilter = {
  limit: -1,
  start: 0,
  fields: ['id', 'name'],
  populate: ['parent'],
};

export type ManufacturerFamily = {
  ids: SerializedManufacturer['id'][] | undefined;
  members: Pick<SerializedManufacturer, 'id' | 'name'>[];
  isLoading: boolean;
};

export const useManufacturerFamily = (manufacturerId: SerializedManufacturer['id'] | undefined): ManufacturerFamily => {
  const { data, isFetching: isLoading } = useManufacturers(FAMILY_FILTER, { enabled: Boolean(manufacturerId) });

  return useMemo(() => {
    if (!manufacturerId) return { ids: undefined, members: [], isLoading: false };

    const rows = data?.data ?? [];
    const ids = [...getManufacturerFamilyIds(rows, manufacturerId)];

    const members = rows
      .filter(({ id }) => ids.includes(id))
      .sort(
        (a, b) => Number(b.id === manufacturerId) - Number(a.id === manufacturerId) || a.name.localeCompare(b.name),
      );

    return { ids, members, isLoading };
  }, [data, isLoading, manufacturerId]);
};
